import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import GuestLayout from "../../Layouts/GuestLayout";
import {
  TextField,
  Snackbar,
  Alert,
  InputAdornment,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import SecondaryButton from "../../Components/SecondaryButton";
import GroupedContainer from "../../Components/GroupedContainer";
import SignatureCanvas from "react-signature-canvas";

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const sigCanvas = useRef(null);

  const [inviteLoading, setInviteLoading] = useState(true);
  const [inviteError, setInviteError] = useState("");

  const [inviteInfo, setInviteInfo] = useState({
    email: "",
    partner_company_id: null,
    partner_company_name: "",
    user_role_id: null,
    user_role_name: "",
    requires_driver_fields: false,
    expires_at: "",
  });

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    phone: "",
    password: "",
    confirmPassword: "",
    trailerType: "Open",
    profilePicture: null,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    const parts = [];
    if (digits.length > 0) parts.push(digits.slice(0, 3));
    if (digits.length > 3) parts.push(digits.slice(3, 6));
    if (digits.length > 6) parts.push(digits.slice(6, 10));
    return parts.join("-");
  };

  const handleFieldChange = (field) => (e) => {
    let value = e.target.value;

    if (field === "phone") {
      value = formatPhoneNumber(value);
    }

    setFormData((prev) => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSelectChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const clearSignature = () => {
    try {
      sigCanvas.current?.clear?.();
    } catch (e) {}
  };

  const showSnack = (message, severity = "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const loadInvitePreview = async () => {
    if (!token || token.trim().length < 20) {
      setInviteError("Invalid invite link (missing token).");
      setInviteLoading(false);
      return;
    }

    setInviteLoading(true);
    setInviteError("");

    try {
      const res = await axios.get("https://new.cajuu.app/api/staff-invites/preview", {
        params: { token },
      });

      const data = res.data?.data || {};
      setInviteInfo({
        email: data.email || "",
        partner_company_id: data.partner_company_id ?? null,
        partner_company_name: data.partner_company_name || "",
        user_role_id: data.user_role_id ?? null,
        user_role_name: data.user_role_name || "",
        requires_driver_fields: !!data.requires_driver_fields,
        expires_at: data.expires_at || "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.data ||
        "Unable to validate invite. It may be expired or already used.";
      setInviteError(String(msg));
    } finally {
      setInviteLoading(false);
    }
  };

  useEffect(() => {
    loadInvitePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const validateFields = () => {
    const errs = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;

    if (!inviteInfo.email || !emailRegex.test(inviteInfo.email)) {
      errs.invite = "Invalid invite email.";
    }

    if (!formData.firstName) errs.firstName = "First name is required";
    if (!formData.lastName) errs.lastName = "Last name is required";
    if (!formData.username) errs.username = "Username is required";

    if (formData.phone && !phoneRegex.test(formData.phone)) {
      errs.phone = "Phone must be in the format 999-999-9999";
    }

    if (!formData.password) {
      errs.password = "Password is required";
    } else if (formData.password.length < 6) {
      errs.password = "Password must be at least 6 characters";
    }

    if (!formData.confirmPassword) {
      errs.confirmPassword = "Confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      errs.confirmPassword = "Passwords do not match";
    }

    if (inviteInfo.requires_driver_fields) {
      if (!formData.trailerType) errs.trailerType = "Trailer type is required";
      if (sigCanvas.current?.isEmpty?.() === true) {
        errs.signature = "Signature is required";
      }
    }

    return errs;
  };

  const buildSignatureBlob = () => {
    if (!inviteInfo.requires_driver_fields) return null;
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) return null;

    const dataURL = sigCanvas.current.toDataURL("image/png");
    const byteString = atob(dataURL.split(",")[1]);
    const mimeString = dataURL.split(",")[0].split(":")[1].split(";")[0];
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uint8Array = new Uint8Array(arrayBuffer);

    for (let i = 0; i < byteString.length; i++) {
      uint8Array[i] = byteString.charCodeAt(i);
    }

    return new Blob([uint8Array], { type: mimeString });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setErrors({});
    setSnackbarMessage("");

    const fieldErrors = validateFields();
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      showSnack(Object.values(fieldErrors)[0], "error");
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = new FormData();

      submitData.append("token", token);
      submitData.append("first_name", formData.firstName);
      submitData.append("last_name", formData.lastName);
      submitData.append("username", formData.username);
      submitData.append("phone", formData.phone || "");
      submitData.append("password", formData.password);
      submitData.append("c_password", formData.confirmPassword);

      if (formData.profilePicture) {
        submitData.append("profile_picture", formData.profilePicture);
      }

      if (inviteInfo.requires_driver_fields) {
        submitData.append("trailer_type", formData.trailerType);

        const signatureBlob = buildSignatureBlob();
        if (signatureBlob) {
          submitData.append("signature", signatureBlob, "signature.png");
        }
      }

      await axios.post("https://new.cajuu.app/api/staff-invites/accept", submitData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      localStorage.setItem("registrationSuccess", "true");
      localStorage.setItem("prefillEmail", inviteInfo.email || "");
      window.location.href = "/login";
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        (typeof err?.response?.data?.data === "string" ? err.response.data.data : "") ||
        "Registration failed.";
      showSnack(String(msg), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <GuestLayout title="Accept Invite">
      <div className="w-full mt-4 sm:mt-5 flex justify-center items-center">
        <div className="flex text-xl sm:text-2xl md:text-3xl tracking-tight text-amber-500 font-semibold">
          Accept invite to
          <div className="text-white flex items-center">
            <img src="/img/cajuu.png" alt="Cajuu Logo" className="w-7 h-7 sm:w-9 sm:h-9 ml-2" />
            Cajuu<span className="text-[#2f2f2f]/40">.</span>
          </div>
        </div>
      </div>

      {inviteLoading ? (
        <GroupedContainer className="w-full mt-6">
          <div className="text-white">Validating invite...</div>
        </GroupedContainer>
      ) : inviteError ? (
        <GroupedContainer className="w-full mt-6">
          <div className="text-red-500 font-semibold">Invite Error</div>
          <div className="text-white mt-2">{inviteError}</div>

          <div className="text-white/70 text-sm mt-4">
            Please contact your company admin and request a new invite.
          </div>

          <div className="mt-6">
            <a href="/login" className="underline underline-offset-4 text-sm font-medium text-white hover:text-amber-500">
              Go to Login
            </a>
          </div>
        </GroupedContainer>
      ) : (
        <>
          {/* Prefill info */}
          <GroupedContainer className="w-full mt-6">
            <div className="grid grid-cols-1 gap-4 sm:gap-6">
              <TextField
                label="Company"
                color="secondary"
                value={inviteInfo.partner_company_name || ""}
                className="w-full"
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Role"
                color="secondary"
                value={inviteInfo.user_role_name || ""}
                className="w-full"
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Email"
                color="secondary"
                value={inviteInfo.email || ""}
                className="w-full"
                InputProps={{ readOnly: true }}
              />
            </div>
          </GroupedContainer>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <GroupedContainer className="w-full mt-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 w-full gap-4 sm:gap-6 items-center">
                <TextField
                  label="First Name"
                  required
                  color="secondary"
                  value={formData.firstName}
                  onChange={handleFieldChange("firstName")}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  className="w-full"
                />
                <TextField
                  label="Last Name"
                  required
                  color="secondary"
                  value={formData.lastName}
                  onChange={handleFieldChange("lastName")}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  className="w-full"
                />
              </div>
            </GroupedContainer>

            <GroupedContainer className="w-full mt-6">
              <div className="grid grid-cols-1 gap-4 sm:gap-6">
                <TextField
                  label="Username"
                  required
                  color="secondary"
                  value={formData.username}
                  onChange={handleFieldChange("username")}
                  error={!!errors.username}
                  helperText={errors.username}
                  className="w-full"
                />

                <TextField
                  label="Phone"
                  color="secondary"
                  value={formData.phone}
                  onChange={handleFieldChange("phone")}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  className="w-full"
                />

                <TextField
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  required
                  color="secondary"
                  value={formData.password}
                  onChange={handleFieldChange("password")}
                  error={!!errors.password}
                  helperText={errors.password}
                  className="w-full"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                          {showPassword ? <VisibilityOff className="text-white" /> : <Visibility className="text-white" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Confirm Password"
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  color="secondary"
                  value={formData.confirmPassword}
                  onChange={handleFieldChange("confirmPassword")}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  className="w-full"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirmPassword((prev) => !prev)} edge="end">
                          {showConfirmPassword ? (
                            <VisibilityOff className="text-white" />
                          ) : (
                            <Visibility className="text-white" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Profile Picture (optional) */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Profile Picture (optional)</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="text-white"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setFormData((prev) => ({ ...prev, profilePicture: file }));
                    }}
                  />
                </div>

                {/* Driver fields for role 2 or 4 */}
                {inviteInfo.requires_driver_fields && (
                  <>
                    <FormControl fullWidth color="secondary" error={!!errors.trailerType}>
                      <InputLabel id="trailer-type-label">Trailer Type *</InputLabel>
                      <Select
                        labelId="trailer-type-label"
                        value={formData.trailerType}
                        label="Trailer Type *"
                        onChange={handleSelectChange("trailerType")}
                        className="w-full"
                      >
                        <MenuItem value="Open">Open</MenuItem>
                        <MenuItem value="Enclosed">Enclosed</MenuItem>
                      </Select>
                      {errors.trailerType && <p className="text-red-500 text-xs mt-1">{errors.trailerType}</p>}
                    </FormControl>

                    <div className="mt-2">
                      <label className="block text-sm font-medium text-white mb-2">Signature *</label>

                      <div className="border border-gray-600 rounded-lg bg-transparent w-full">
                        <SignatureCanvas
                          ref={sigCanvas}
                          penColor="white"
                          canvasProps={{
                            height: 180,
                            className: "w-full rounded-lg border border-zinc-600 bg-transparent",
                          }}
                        />
                      </div>

                      <div className="flex justify-end mt-2">
                        <SecondaryButton onClick={clearSignature} className="text-gray-400 hover:text-white text-sm">
                          Clear Signature
                        </SecondaryButton>
                      </div>

                      {errors.signature && <p className="text-red-500 text-xs mt-1">{errors.signature}</p>}
                    </div>
                  </>
                )}
              </div>
            </GroupedContainer>

            <div className="mt-6 sm:mt-10">
              <SecondaryButton
                type="submit"
                disabled={isSubmitting}
                className={`bg-amber-500 hover:bg-amber-600 hover:text-white border-none h-12 text-slate-900 w-full justify-center transition-transform duration-200 ease-in-out hover:scale-95 active:scale-95 ${
                  isSubmitting ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {isSubmitting ? "Submitting..." : "Complete Registration"}
              </SecondaryButton>

              <div className="flex w-full justify-center sm:flex-row items-center mt-6 sm:mt-8">
                <a
                  href="/login"
                  className="underline underline-offset-4 hover:decoration-4 text-sm font-medium text-white hover:text-amber-500 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Back to Login
                </a>
              </div>
            </div>
          </form>
        </>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{
            width: "500px",
            backgroundColor: snackbarSeverity === "success" ? "#16a34a" : "#dc2626",
            color: "#fff",
            "& .MuiAlert-icon": { color: "#fff" },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </GuestLayout>
  );
}
